import VueRouter, { Route } from 'vue-router';
import { GtmSupport, GtmSupportOptions } from '@gtm-support/core';
import LocalizationUtils from '~/localization/LocalizationUtils';
import UserConsentContext, { CONSENT_OPTIONS } from '~/services/UserConsentContext';
import URLS from '~/URLS';

interface VueGTMOptions extends GtmSupportOptions {
    router: VueRouter;
    ignoredViews?: Array<string>;
}

export default class GTM {
    static options: VueGTMOptions;
    private static instance: GtmSupport;

    static init(router: VueRouter) {
        const enabled = UserConsentContext.getOption(CONSENT_OPTIONS.ANALYTICS);

        const options: VueGTMOptions = {
            id: process.env.VUE_APP_GTM_ID, // Your GTM single container ID, array of container ids ['GTM-xxxxxx', 'GTM-yyyyyy'] or array of objects [{id: 'GTM-xxxxxx', queryParams: { gtm_auth: 'abc123', gtm_preview: 'env-4', gtm_cookies_win: 'x'}}, {id: 'GTM-yyyyyy', queryParams: {gtm_auth: 'abc234', gtm_preview: 'env-5', gtm_cookies_win: 'x'}}], // Your GTM single container ID or array of container ids ['GTM-xxxxxx', 'GTM-yyyyyy']
            defer: false, // Script can be set to `defer` to speed up page load at the cost of less accurate results (in case visitor leaves before script is loaded, which is unlikely but possible). Defaults to false, so the script is loaded `async` by default
            compatibility: false, // Will add `async` and `defer` to the script tag to not block requests for old browsers that do not support `async`
            enabled: enabled,
            debug: false, // Whether or not display console logs debugs (optional)
            loadScript: true, // Whether or not to load the GTM Script (Helpful if you are including GTM manually, but need the dataLayer functionality in your components) (optional)
            router: router, // Pass the router instance to automatically sync with router (optional)
            trackViewEventProperty: 'page_view',
            ignoredViews: [URLS.redirect, URLS.recoverPassword, URLS.recoverUser], // Don't trigger events for specified router names (optional)
        };

        GTM.options = options;
        GTM.instance = new GtmSupport(options);
        GTM.initRouter(options.router);
        GTM.enable(enabled);
    }

    static trackView(name: string, fullUrl: string) {
        GTM.instance.trackView(name, fullUrl, {
            pageTitle: document.title,
        });
    }

    static trackEvent(
        event: string,
        category: string,
        action: string,
        label: string,
        value: object
    ) {
        GTM.instance.trackEvent({
            event,
            category,
            action,
            label,
            ...value,
        });
    }

    static enable(enable = true) {
        GTM.instance.enable(enable);
    }

    private static initRouter(router: VueRouter) {
        const ignoredViews = GTM.options.ignoredViews || [];

        router.afterEach(async (to, from) => {
            // Ignore some routes
            if (
                typeof to.name !== 'string' ||
                (Array.isArray(ignoredViews) &&
                    (ignoredViews.includes(to.name) ||
                        ignoredViews.find(view => to.path.startsWith(view))))
            ) {
                return;
            }

            // Dispatch vue event using meta gtm value if defined otherwise fallback to route name
            const name: string =
                to.meta && typeof to.meta.gtm === 'string' && !!to.meta.gtm ? to.meta.gtm : to.name;

            const locale = LocalizationUtils.getLocaleFromURL();

            const baseUrl = `/${locale}/`;
            let fullUrl: string = baseUrl;
            fullUrl += to.fullPath.startsWith('/') ? to.fullPath.substring(1) : to.fullPath;
            GTM.instance.trackView(name, fullUrl);
        });
    }
}
